import { SPARTAN_REVEAL_READY_CLASS } from '@/app/lcv/components/sections/spartan/products/shared/reveal';

const REVEAL_SELECTOR = '[data-spartan-reveal]';
const MODAL_REVEAL_ROOT_SELECTOR = '[data-spartan-modal-reveal-root]';
const REVEAL_CLASS_NAME = 'is-inview';
const DEFAULT_REVEAL_OFFSET = '20%';

const getRevealRootMargin = (element: HTMLElement) => {
  const offset =
    element.dataset.spartanRevealOffset?.split(',')[0]?.trim() ||
    DEFAULT_REVEAL_OFFSET;

  return `0px 0px -${offset} 0px`;
};

const getRevealElements = (node: Node): HTMLElement[] => {
  if (!(node instanceof HTMLElement)) return [];

  const elements = node.matches(REVEAL_SELECTOR)
    ? [node]
    : Array.from(node.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));

  return elements.filter(
    (element) => !element.closest(MODAL_REVEAL_ROOT_SELECTOR)
  );
};

export const observeSpartanProductReveals = (): (() => void) => {
  const observed = new Map<HTMLElement, IntersectionObserver>();
  const observers = new Map<string, IntersectionObserver>();

  const reveal = (element: HTMLElement) => {
    element.classList.add(REVEAL_CLASS_NAME);
    observed.get(element)?.unobserve(element);
    observed.delete(element);
  };

  const observe = (element: HTMLElement) => {
    if (
      element.classList.contains(REVEAL_CLASS_NAME) ||
      observed.has(element)
    ) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      reveal(element);
      return;
    }

    const rootMargin = getRevealRootMargin(element);
    let observer = observers.get(rootMargin);

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) reveal(entry.target as HTMLElement);
          });
        },
        { rootMargin }
      );
      observers.set(rootMargin, observer);
    }

    observed.set(element, observer);
    observer.observe(element);
  };

  getRevealElements(document.body).forEach(observe);

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        getRevealElements(node).forEach(observe);
      });
    });
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  const readyFrame = window.requestAnimationFrame(() => {
    document.documentElement.classList.add(SPARTAN_REVEAL_READY_CLASS);
  });

  return () => {
    window.cancelAnimationFrame(readyFrame);
    mutationObserver.disconnect();
    observers.forEach((observer) => observer.disconnect());
    document.documentElement.classList.remove(SPARTAN_REVEAL_READY_CLASS);
  };
};
